import type { LayerSelection } from './data-layer-state'

/**
 * Where the user is on the map page.
 *
 * `BrowseFields`: pre-field state — viewing the operation's field list with
 * optional client/farm filtering. The map preview shows field outlines.
 *
 * `InField`: a layer is selected. `mainMap` is always present; `comparisonMap`
 * is set when split-map is active (its `disabled` flag preserves the layer
 * choice when split visibility is toggled off).
 */
export type Session = BrowseFields | InField

/**
 * Which map pane something addresses. The values double as the keys of the
 * per-pane records on `MapState`, so a pane can be carried as a value and then
 * used to index one without a second spelling in between.
 */
export enum Pane {
  Main = 'main',
  Comparison = 'comparison'
}

export type BrowseFields = {
  kind: 'BrowseFields'
  filter: FilterState
  seasonFilter: SeasonFilter | null
  listView: BrowseListView
}

export type InField = {
  kind: 'InField'
  mainMap: LayerSelection
  comparisonMap: { selection: LayerSelection; disabled: boolean } | null
}

/**
 * Our UI assumes that layer options are scoped to a particular crop cycle.
 * Sometimes this is derivable from the layer itself (a stream), other times
 * it is only partially derivable (a plan asset, a satellite capture, which have
 * a season but no crop).
 */
export type PaneScope = {
  season: number
  cropKey: string
}

/**
 * Client / farm filter for the BrowseFields list. The two filters are not
 * independent — selecting a Farm always carries its parent Client, and there
 * is no way to filter by Farm without a Client.
 *
 * The absent fields are typed `?: undefined` (rather than omitted) so callers
 * can read `filter.client` / `filter.farm` as `string | undefined` without
 * first narrowing on `level` — `level` remains the discriminant, so narrowing
 * still recovers the exact per-variant shape.
 */
export type FilterState =
  | { level: 'None'; client?: undefined; farm?: undefined }
  | { level: 'Client'; client: string; farm?: undefined }
  | { level: 'Farm'; client: string; farm: string }

export const noFilter: FilterState = { level: 'None' }

/**
 * Optional season filter on the BrowseFields list. Restricts the list to
 * fields with at least one stream in that season. `cropKey` is the agronomy
 * domain's crop identifier (e.g. "corn", "soybeans"); `year` is the crop year.
 */
export type SeasonFilter = {
  year: number
  cropKey: string
}

export type BrowseListView = 'ByTime' | 'ByField'

export const defaultBrowseFields: BrowseFields = {
  kind: 'BrowseFields',
  filter: noFilter,
  seasonFilter: null,
  listView: 'ByField'
}
